import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createInvalidationBuendler } from '@/lib/debouncedInvalidate';

/**
 * Anzahl der Konversationen mit ungelesenen Nachrichten -- fuer das Badge in
 * der Seitenleiste.
 *
 * Die Seitenleiste hat dafuer frueher `useConversations` benutzt und damit die
 * komplette Liste geladen (bei M&M Hairdesign 1.951 Konversationen samt
 * Nachrichtentexten), nur um `unreadCount > 0` zu zaehlen. Weil die
 * Seitenleiste im Layout steckt, passierte das auf *jeder* Seite und bei jeder
 * eingehenden Nachricht erneut.
 *
 * Die RPC `unread_conversation_count` liefert stattdessen eine einzelne Zahl,
 * mit demselben Unread-Begriff wie die View conversation_summaries.
 */
export const unreadConversationsCountKey = (tenantId: string | null) =>
  ['unread-conversations-count', tenantId] as const;

/**
 * Holt die Zahl aus der Datenbank. Bewusst als eigene Funktion, damit sie ohne
 * React getestet werden kann.
 *
 * Die RPC liefert NULL, wenn der Tenant nicht sichtbar ist (RLS) -- daraus wird
 * hier 0, damit die Seitenleiste kein leeres Badge rendert.
 */
export async function fetchUnreadConversationsCount(
  tenantId: string | null,
  client: Pick<typeof supabase, 'rpc'> = supabase
): Promise<number> {
  if (!tenantId) return 0;

  const { data, error } = await client.rpc('unread_conversation_count' as never, {
    p_tenant: tenantId,
  } as never);

  if (error) throw error;

  const count = Number(data ?? 0);
  return Number.isFinite(count) ? count : 0;
}

export function useUnreadConversationsCount(tenantId: string | null) {
  const queryClient = useQueryClient();

  const { data: unreadCount = 0, isLoading } = useQuery({
    queryKey: unreadConversationsCountKey(tenantId),
    queryFn: () => fetchUnreadConversationsCount(tenantId),
    enabled: !!tenantId,
  });

  // Dieselben Broadcast-Topics wie die Inbox, damit das Badge live bleibt.
  // Die Topic-Namen muessen exakt uebereinstimmen -- sie bestimmen, welche
  // Ereignisse ankommen.
  useEffect(() => {
    if (!tenantId) return undefined;

    // Gebuendelt statt sofort: Pro Nachricht feuern zwei Kanaele, und die
    // Nachrichten kommen in Schueben. Ohne Buendelung laedt dieses Badge --
    // das in der Seitenleiste auf *jeder* Seite haengt -- bei jedem einzelnen
    // Ereignis neu. Etwas grosszuegiger als die Inbox: eine Zahl darf ein paar
    // Sekunden nachlaufen.
    const buendler = createInvalidationBuendler(queryClient, 4000);
    const invalidate = () => buendler.anfordern(unreadConversationsCountKey(tenantId));

    const conversationsChannel = supabase
      .channel(`tenant:${tenantId}:conversations`, { config: { private: true } })
      .on('broadcast', { event: '*' }, invalidate);

    const messagesChannel = supabase
      .channel(`tenant:${tenantId}:messages`, { config: { private: true } })
      .on('broadcast', { event: '*' }, invalidate);

    const setupChannels = async () => {
      await supabase.realtime.setAuth();
      await conversationsChannel.subscribe();
      await messagesChannel.subscribe();
    };

    setupChannels();

    return () => {
      buendler.abbrechen();
      supabase.removeChannel(conversationsChannel);
      supabase.removeChannel(messagesChannel);
    };
  }, [tenantId, queryClient]);

  return { unreadCount, isLoading };
}
